import { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  Circle as CircleIcon,
  Loader2,
  Pencil,
  Save,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";

type Tool = "pen" | "arrow" | "circle";

interface Point {
  x: number;
  y: number;
}

type Stroke =
  | { kind: "pen"; points: Point[] }
  | { kind: "arrow"; from: Point; to: Point }
  | { kind: "circle"; from: Point; to: Point };

export interface PhotoAnnotatorProps {
  imageSrc: string;
  saving?: boolean;
  onCancel: () => void;
  onSave: (dataUrl: string) => void | Promise<void>;
}

const STROKE_COLOR = "#E2553D";

export function PhotoAnnotator({
  imageSrc,
  saving = false,
  onCancel,
  onSave,
}: PhotoAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const liveStrokeRef = useRef<Stroke | null>(null);
  const isDrawingRef = useRef(false);

  const [imageReady, setImageReady] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [tool, setTool] = useState<Tool>("pen");
  const [strokes, setStrokes] = useState<Stroke[]>([]);

  // Bild laden und Canvas initialisieren
  useEffect(() => {
    setImageReady(false);
    setImageError(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
      }
      setImageReady(true);
    };
    img.onerror = () => setImageError(true);
    img.src = imageSrc;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageSrc]);

  // Re-render bei jeder Änderung
  useEffect(() => {
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes, imageReady]);

  function getStrokeWidth(): number {
    const img = imageRef.current;
    if (!img) return 4;
    const base = Math.min(img.naturalWidth || 1000, img.naturalHeight || 1000);
    return Math.max(3, Math.round(base / 250));
  }

  function redraw(extra?: Stroke | null) {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const lw = getStrokeWidth();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = STROKE_COLOR;
    ctx.fillStyle = STROKE_COLOR;
    ctx.lineWidth = lw;

    const all: Stroke[] = extra ? [...strokes, extra] : strokes;
    for (const s of all) {
      if (s.kind === "pen") drawPen(ctx, s);
      else if (s.kind === "arrow") drawArrow(ctx, s, lw);
      else drawEllipse(ctx, s);
    }
  }

  function drawPen(
    ctx: CanvasRenderingContext2D,
    s: { kind: "pen"; points: Point[] },
  ) {
    if (s.points.length === 0) return;
    if (s.points.length === 1) {
      const p = s.points[0];
      ctx.beginPath();
      ctx.arc(p.x, p.y, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(s.points[0].x, s.points[0].y);
    for (let i = 1; i < s.points.length; i++) {
      ctx.lineTo(s.points[i].x, s.points[i].y);
    }
    ctx.stroke();
  }

  function drawArrow(
    ctx: CanvasRenderingContext2D,
    s: { from: Point; to: Point },
    lw: number,
  ) {
    const { from, to } = s;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy);
    if (len < 2) return;
    const headLen = Math.max(lw * 4, 18);
    const ang = Math.atan2(dy, dx);
    const a1 = ang + Math.PI - Math.PI / 7;
    const a2 = ang + Math.PI + Math.PI / 7;
    ctx.beginPath();
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(to.x + Math.cos(a1) * headLen, to.y + Math.sin(a1) * headLen);
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(to.x + Math.cos(a2) * headLen, to.y + Math.sin(a2) * headLen);
    ctx.stroke();
  }

  function drawEllipse(
    ctx: CanvasRenderingContext2D,
    s: { from: Point; to: Point },
  ) {
    const { from, to } = s;
    const cx = (from.x + to.x) / 2;
    const cy = (from.y + to.y) / 2;
    const rx = Math.abs(to.x - from.x) / 2;
    const ry = Math.abs(to.y - from.y) / 2;
    if (rx < 1 || ry < 1) return;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  function pointerToCanvas(e: React.PointerEvent<HTMLCanvasElement>): Point {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / Math.max(rect.width, 1);
    const sy = canvas.height / Math.max(rect.height, 1);
    return {
      x: (e.clientX - rect.left) * sx,
      y: (e.clientY - rect.top) * sy,
    };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!imageReady) return;
    e.preventDefault();
    try {
      (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    } catch {
      // Some browsers throw if capture is unsupported – ignore safely
    }
    isDrawingRef.current = true;
    const p = pointerToCanvas(e);
    if (tool === "pen") {
      liveStrokeRef.current = { kind: "pen", points: [p] };
    } else if (tool === "arrow") {
      liveStrokeRef.current = { kind: "arrow", from: p, to: p };
    } else {
      liveStrokeRef.current = { kind: "circle", from: p, to: p };
    }
    redraw(liveStrokeRef.current);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current || !liveStrokeRef.current) return;
    e.preventDefault();
    const p = pointerToCanvas(e);
    const live = liveStrokeRef.current;
    if (live.kind === "pen") {
      live.points.push(p);
    } else {
      live.to = p;
    }
    redraw(live);
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    isDrawingRef.current = false;
    const live = liveStrokeRef.current;
    liveStrokeRef.current = null;
    if (!live) return;
    // Verwerfe sehr kleine Versehen (außer beim Pen-Tool, wo ein Tipp ein Punkt sein darf)
    if (live.kind !== "pen") {
      const len = Math.hypot(live.to.x - live.from.x, live.to.y - live.from.y);
      if (len < 4) return;
    }
    setStrokes((prev) => [...prev, live]);
  }

  function handleUndo() {
    setStrokes((prev) => prev.slice(0, -1));
  }

  function handleClear() {
    setStrokes([]);
  }

  async function handleSave() {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return;
    redraw();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    await onSave(dataUrl);
  }

  const hasStrokes = strokes.length > 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Foto markieren"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-2 py-2 bg-black/80 text-white">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onCancel}
          disabled={saving}
          className="text-white hover:bg-white/10 h-11 w-11"
          aria-label="Markieren abbrechen"
          data-testid="button-photo-annotator-close"
        >
          <X className="w-6 h-6" />
        </Button>
        <span className="text-sm font-medium">Foto markieren</span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleUndo}
            disabled={!hasStrokes || saving}
            className="text-white hover:bg-white/10 h-11 w-11 disabled:opacity-40"
            aria-label="Letzte Markierung rückgängig"
            data-testid="button-photo-annotator-undo"
          >
            <Undo2 className="w-5 h-5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            disabled={!hasStrokes || saving}
            className="text-white hover:bg-white/10 h-11 w-11 disabled:opacity-40"
            aria-label="Alle Markierungen entfernen"
            data-testid="button-photo-annotator-clear"
          >
            <Trash2 className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-hidden flex items-center justify-center bg-black">
        {imageError ? (
          <div className="text-center text-white text-sm px-6">
            Foto konnte nicht geladen werden.
          </div>
        ) : !imageReady ? (
          <div className="text-center text-white text-sm flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Foto wird geladen…
          </div>
        ) : null}
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          className="max-w-full max-h-full"
          style={{
            touchAction: "none",
            display: imageReady && !imageError ? "block" : "none",
            objectFit: "contain",
          }}
          data-testid="canvas-photo-annotator"
        />
      </div>

      {/* Bottom toolbar */}
      <div className="bg-black/85 text-white px-3 py-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex flex-col gap-2">
        <div className="flex items-center justify-center gap-2">
          <ToolButton
            active={tool === "pen"}
            onClick={() => setTool("pen")}
            label="Frei zeichnen"
            testId="button-photo-annotator-tool-pen"
          >
            <Pencil className="w-5 h-5" />
            <span className="text-xs">Stift</span>
          </ToolButton>
          <ToolButton
            active={tool === "arrow"}
            onClick={() => setTool("arrow")}
            label="Pfeil"
            testId="button-photo-annotator-tool-arrow"
          >
            <ArrowUpRight className="w-5 h-5" />
            <span className="text-xs">Pfeil</span>
          </ToolButton>
          <ToolButton
            active={tool === "circle"}
            onClick={() => setTool("circle")}
            label="Kreis"
            testId="button-photo-annotator-tool-circle"
          >
            <CircleIcon className="w-5 h-5" />
            <span className="text-xs">Kreis</span>
          </ToolButton>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!imageReady || saving || !hasStrokes}
          className="bg-bauwerk-orange hover:bg-bauwerk-light-orange py-4 h-auto text-base"
          data-testid="button-photo-annotator-save"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Speichere Markierung…
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Markierung speichern
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

interface ToolButtonProps {
  active: boolean;
  label: string;
  testId: string;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolButton({ active, label, testId, onClick, children }: ToolButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      data-testid={testId}
      className={`flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-md min-w-[64px] transition-colors ${
        active
          ? "bg-bauwerk-orange text-white"
          : "bg-white/10 text-white hover:bg-white/20"
      }`}
    >
      {children}
    </button>
  );
}
